import { useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Modal, OverlayTrigger, Popover, PopoverContent } from 'react-bootstrap';
import { useSecurity } from '../Security/security';
import { syncBusinessPartnerToJira } from '../../api/businessPartner';

const DISABLED_MSG = 'Company Name, EORI and Status must be valid to Sync with Jira';
const SYNC_FAILURE_MSG = 'Jira sync unsuccessful. Please try again.';
const REQUIRED_FIELDS = ['Company Name', 'Trader EORI Number', 'CABIE Status'];

const getFieldValue = (localBPData, subtitle) => {
  for (const section of Object.values(localBPData || {})) {
    if (!Array.isArray(section)) continue;
    const item = section.find((row) => row?.subtitle === subtitle);
    if (item?.value != null && String(item.value).trim()) return String(item.value).trim();
  }
  return '';
};

const canSync = (localBPData, errors) =>
  REQUIRED_FIELDS.every((field) => getFieldValue(localBPData, field) && !errors?.[field]);

const parseSyncErrorMessage = (value, fallback = SYNC_FAILURE_MSG) => {
  const text = typeof value === 'string' ? value : value?.message;
  if (!text?.trim()) return fallback;

  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === 'object') {
      return parsed.error || parsed.message || parsed.err || fallback;
    }
  } catch {
    // response was plain text, not JSON
  }

  return text;
};

const hasNoPreviousSync = ({ jiraSyncDate } = {}) => {
  const syncDate = jiraSyncDate != null ? dayjs(jiraSyncDate) : null;
  return !syncDate?.isValid();
};

const formatTooltip = ({ jiraSyncDate, jiraSyncUser }) => {
  const syncDate = jiraSyncDate != null ? dayjs(jiraSyncDate) : null;

  if (!syncDate?.isValid()) {
    return { title: 'No previous sync', subtitle: '' };
  }

  return {
    title: `Last sync: ${syncDate.format('DD-MM-YYYY HH:mm')}`,
    subtitle: jiraSyncUser ?? '',
  };
};

const BPSyncJiraButton = ({ businessPartnerId, localBPData, errors, jiraSyncInfo, onSyncSuccess }) => {
  const { validateRole } = useSecurity();
  const [isSyncing, setIsSyncing] = useState(false);
  const [failureMessage, setFailureMessage] = useState('');

  const allowedRole = validateRole({
    or: [
      { department: 'OPS', role: 'SUPERUSER' },
      { department: 'QA', role: 'SUPERUSER' },
      { department: 'CMT' },
    ],
  });
  const canShow =
    allowedRole && businessPartnerId != null && businessPartnerId !== 0 && jiraSyncInfo?.jiraSyncRequired != null;
  const isValid = canSync(localBPData, errors);
  const tooltip = formatTooltip(jiraSyncInfo ?? {});

  if (!canShow) return null;

  const colourClass = !isValid ? 'grey' : jiraSyncInfo.jiraSyncRequired || hasNoPreviousSync(jiraSyncInfo) ? 'yellow' : '';

  const handleSync = async () => {
    if (!isValid || isSyncing) return;
    setIsSyncing(true);
    try {
      const res = await syncBusinessPartnerToJira(businessPartnerId);
      const row = Array.isArray(res) ? res[0] : res;
      if (row?.success === false || row?.success === 'failed') {
        setFailureMessage(parseSyncErrorMessage(row?.error || row?.message || row?.err));
        return;
      }
      await onSyncSuccess?.();
    } catch (error) {
      setFailureMessage(parseSyncErrorMessage(error));
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <>
      <span className="bp-sync-jira-controls">
        <OverlayTrigger
          placement="top"
          trigger={isValid ? [] : ['hover', 'focus']}
          overlay={
            <Popover>
              <PopoverContent>{DISABLED_MSG}</PopoverContent>
            </Popover>
          }
        >
          <span>
            <button
              type="button"
              className={`stepper-button bp-sync-jira-button ${colourClass}`}
              onClick={handleSync}
              disabled={!isValid || isSyncing}
              data-testid="sync-jira-button"
            >
              {isSyncing ? 'Syncing...' : 'Sync Jira'}
            </button>
          </span>
        </OverlayTrigger>
        <OverlayTrigger
          placement="top"
          trigger={['hover', 'focus']}
          overlay={
            <Popover>
              <PopoverContent>
                <div>{tooltip.title}</div>
                {tooltip.subtitle ? <div>{tooltip.subtitle}</div> : null}
              </PopoverContent>
            </Popover>
          }
        >
          <span className="bp-sync-jira-info">i</span>
        </OverlayTrigger>
      </span>

      <Modal show={Boolean(failureMessage)} centered animation={false} onHide={() => setFailureMessage('')}>
        <Modal.Header>
          <Modal.Title>Jira sync unsuccessful</Modal.Title>
          <button type="button" className="btn-close" aria-label="Close" onClick={() => setFailureMessage('')} />
        </Modal.Header>
        <Modal.Body>
          <p>{SYNC_FAILURE_MSG}</p>
          {failureMessage !== SYNC_FAILURE_MSG ? <p className="mb-0">{failureMessage}</p> : null}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="stepper-button yellow" onClick={() => setFailureMessage('')}>
            Close
          </button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

BPSyncJiraButton.propTypes = {
  businessPartnerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  localBPData: PropTypes.object,
  errors: PropTypes.object,
  jiraSyncInfo: PropTypes.object,
  onSyncSuccess: PropTypes.func,
};

export default BPSyncJiraButton;
